import React from 'react';
import { Button, colors, Modal } from '@insurancemenu/imenu-components-lib';
import { WarningFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import FormSpinner from 'components/common/loaders/FormSpinner';
import { createStyles as createModalStyles } from 'components/common/modals/ActionModal/styles';
import clsx from 'clsx';
import { toJS } from 'mobx';
import { StatisticsFields } from 'types/electionService';
import { createStyles } from './styles';
import { calculateStatistics } from '../ElectionsStatistics';

interface Props {
  show: boolean;
  onClose: () => void;
  onAction?: () => void;
}
const EndOpenEnrollmentModal = observer(({ show, onClose, onAction }: Props): JSX.Element => {
  const modalStyles = createModalStyles(true); // common modal styles
  const classes = createStyles();

  const {
    employeeElectionsStore: { completeElectionsCheck, modalLoading, statistic },
    domainStoreV2: { domainHeader },
  } = useRootStore();

  const convertedElectionCheck = toJS(completeElectionsCheck);
  const convertedDomain = toJS(domainHeader);
  const convertedStatistic = toJS(statistic);

  const {
    allWithClasses,
    subsWithClass,
    isProductLinesQuestionAnswered,
    isWaiverQuestionAnswered,
    isDependentOnProductLineValid,
    generalQuestionAnswered,
  } = convertedElectionCheck;

  const areQuestionsAnswered = isProductLinesQuestionAnswered && isWaiverQuestionAnswered && generalQuestionAnswered;
  const areClassesValid = subsWithClass === 0 || (convertedDomain.properties.eeClassesRequired ? allWithClasses : true);

  const shouldEndOpenEnrollmentBeBlocked = !areQuestionsAnswered || !areClassesValid || !isDependentOnProductLineValid;

  const statisticFields = calculateStatistics(convertedStatistic);

  const StatisticItems = Object.keys(statisticFields).map((key: string): JSX.Element => {
    const statisticsCard = statisticFields[key as keyof StatisticsFields];
    return (
      <li className={clsx(classes.statisticsItem, key === 'total' && classes.total)} key={key}>
        <span className={classes.statisticValue}>{statisticsCard.value}</span>
        <span className={classes.statisticTitle}>{statisticsCard.title}</span>
      </li>
    );
  });

  return (
    <Modal show={show} onClose={onClose} className={modalStyles.modal} closable>
      <FormSpinner display={modalLoading} />
      <WarningFilled width="5.6rem" className={modalStyles.icon} color={colors.orange[500]} />

      <p className={modalStyles.modalTitle}>End Open Enrollment</p>
      <div className={clsx(modalStyles.contentText, classes.contentText)}>
        <p>
          By proceeding to end open enrollment, any employees who have received an invitation to self-enroll will no
          longer be able to make/update their benefit elections.
        </p>
        <p>
          <b>IMPORTANT: </b>Any employee records without elections will be automatically set to waive coverage for
          available benefits.
        </p>
        <p>
          <b>NOTE: </b>As the administrator, you will still be able to modify elections and add any missing employees up
          until you complete the next step: Review and Submit.
        </p>
      </div>
      <ul className={clsx(classes.statisticsList, !shouldEndOpenEnrollmentBeBlocked && classes.validStatistics)}>
        {StatisticItems}
      </ul>
      {shouldEndOpenEnrollmentBeBlocked && (
        <div className={classes.validationBlock}>
          <b className={classes.validationBlockTitle}>Attention! There are issues with your submission.</b>
          <ul className={classes.validations}>
            {!isProductLinesQuestionAnswered && (
              <li>Additional information is still missing for product lines that have been elected.</li>
            )}
            {!isWaiverQuestionAnswered && <li>There are waivers forms that still need to be completed.</li>}
            {!areClassesValid && <li>All subscribers must have classes.</li>}
            {!isDependentOnProductLineValid && <li>Some employees still need to be elected into Product Line</li>}
            {!generalQuestionAnswered && <li>There are employees with incomplete profiles.</li>}
          </ul>
        </div>
      )}
      <footer className={modalStyles.modalFooter}>
        <Button type="outlined" onClick={onClose} size="large" className={classes.closeBtn}>
          Cancel
        </Button>
        <Button
          className={classes.actionButton}
          color="primary"
          onClick={onAction}
          size="large"
          disabled={shouldEndOpenEnrollmentBeBlocked || modalLoading}
        >
          End open enrollment
        </Button>
      </footer>
    </Modal>
  );
});

export default EndOpenEnrollmentModal;
